import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Tooltip,
  Snackbar,
  Backdrop,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Upload,
  Download,
  Description,
  CheckCircle,
  Error,
  Warning,
  Search,
  Close,
  Business,
  People,
  School,
  Person,
  Assignment,
  MenuBook,
  Visibility,
  FileDownload,
  Refresh,
  AccessTime,
} from "@mui/icons-material";
import DataImportWizard from "../../components/common/DataImportWizard";
import { useDownloadCSVTemplate } from "../../hooks/queries/useImport";
import ImportTypeSelector from "../../components/import-export/ImportTypeSelector";
import { useImportHistory } from "../../hooks/queries/useImport";
import importService from "../../services/importService";
import Pagination from "@mui/material/Pagination";
import { useQueryClient } from "@tanstack/react-query";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { queryKeys } from "../../hooks/queries/queryKeys";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionGate from "../../components/common/PermissionGate";
import {
  ALL_IMPORT_PERMISSION_SLUGS,
  IMPORT_ENTITY_PERMISSION,
} from "../../utils/importExportPermissions";

function dispositionFilename(cd) {
  if (!cd || typeof cd !== "string") return null;
  const m = /filename\*=UTF-8''([^;\n\r]+)|filename="?([^";\n\r]+)"?/i.exec(cd);
  if (!m) return null;
  try {
    return decodeURIComponent((m[1] || m[2] || "").trim());
  } catch {
    return (m[1] || m[2] || "").trim();
  }
}

function effectiveSuccessCount(item) {
  const s = Number(item?.successCount ?? item?.success);
  if (!Number.isNaN(s) && s > 0) return s;
  const t = Number(item?.totalRows);
  const f = Number(item?.failedCount ?? 0);
  if (!Number.isNaN(t) && t >= 0 && !Number.isNaN(f)) return Math.max(0, t - f);
  return 0;
}

const SuperAdminImport = () => {
  const { hasVisiblePermission } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedImportType, setSelectedImportType] = useState("institutes");
  const [importTypeSelectorOpen, setImportTypeSelectorOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [historySearchQuery, setHistorySearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const queryClient = useQueryClient();
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [reportPage, setReportPage] = useState(1);
  const [reportLimit, setReportLimit] = useState(10);
  const [reportDatePreset, setReportDatePreset] = useState("all");
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [filterType, setFilterType] = useState("");
  const { data: statsImportData = { imports: [], pagination: { total: 0 }, overallCounts: { totalRows: 0, successCount: 0, failedCount: 0 } } } = useImportHistory({ page: 1, limit: 1 });
  const statsPagination = statsImportData.pagination || { total: 0 };
  const statsOverallCounts = statsImportData.overallCounts || { totalRows: 0, successCount: 0, failedCount: 0 };
  const { data: historyImportData = { imports: [], pagination: { total: 0, page: 1, limit: 10, totalPages: 0 } }, isLoading: historyLoading, isFetching: historyFetching } = useImportHistory({ page: historyPage, limit: historyLimit, entityType: filterType || undefined });
  const historyPagination = historyImportData.pagination || { total: 0, page: historyPage, limit: historyLimit, totalPages: 0 };

  const reportHistoryParams = useMemo(() => {
    const base = { page: reportPage, limit: reportLimit };
    if (reportDatePreset === "custom") {
      if (reportFrom) base.from = reportFrom;
      if (reportTo) base.to = reportTo;
      return base;
    }
    if (reportDatePreset !== "all") {
      base.period = reportDatePreset;
    }
    return base;
  }, [reportPage, reportLimit, reportDatePreset, reportFrom, reportTo]);

  const { data: reportsImportData = { imports: [], pagination: { total: 0, page: 1, limit: 10, totalPages: 0 } }, isLoading: reportsLoading, isFetching: reportsFetching } = useImportHistory(reportHistoryParams);
  const reportsPagination = reportsImportData.pagination || { total: 0, page: reportPage, limit: reportLimit, totalPages: 0 };

  const allImportTypes = [
    {
      id: "institutes",
      label: "Institutes",
      icon: <Business />,
      color: "primary",
    },
    {
      id: "superadmins",
      label: "Super Admin Users",
      icon: <Person />,
      color: "error",
    },
    {
      id: "departments",
      label: "Departments",
      icon: <School />,
      color: "secondary",
    },
    {
      id: "users",
      label: "Users (All Types)",
      icon: <People />,
      color: "info",
    },
    { id: "courses", label: "Courses", icon: <MenuBook />, color: "success" },
    {
      id: "subjects",
      label: "Subjects",
      icon: <Assignment />,
      color: "warning",
    },
  ];

  const importTypes = useMemo(
    () =>
      allImportTypes.filter((type) => {
        const perm = IMPORT_ENTITY_PERMISSION[type.id];
        return !perm || hasVisiblePermission(perm);
      }),
    [hasVisiblePermission]
  );

  const downloadTemplateMutation = useDownloadCSVTemplate();

  const performDownload = async (actualType, displayName) => {
    try {
      const response = await downloadTemplateMutation.mutateAsync(actualType || displayName);
      const payload = response?.data !== undefined ? response.data : response;
      const hdrs = response?.headers || {};
      const dispo = hdrs["content-disposition"] || hdrs["Content-Disposition"];
      const parsedName = dispositionFilename(dispo);
      let blob;
      let fileName = parsedName || `${displayName || actualType || "template"}`;
      if (payload instanceof Blob) {
        let name = parsedName;
        if (!name || !/\.[a-z0-9]+$/i.test(name)) {
          const ab = await payload.slice(0, 2).arrayBuffer();
          const u = new Uint8Array(ab);
          const xlsx = u[0] === 0x50 && u[1] === 0x4b;
          name = xlsx ? `${displayName || actualType}_import_template.xlsx` : `${displayName || actualType}_import_template.csv`;
        }
        blob = payload;
        fileName = name;
      } else if (payload && typeof payload.csvData === "string") {
        blob = new Blob([payload.csvData], { type: "text/csv;charset=utf-8" });
        const fromServer = payload.fileName;
        fileName = fromServer && /\.csv$/i.test(fromServer) ? fromServer : `${displayName || actualType}_import_template.csv`;
      } else if (typeof payload === "string") {
        blob = new Blob([payload], { type: "text/csv;charset=utf-8" });
        fileName = `${displayName || actualType}_import_template.csv`;
      } else {
        setSnackbar({ open: true, message: "Unexpected template response from server", severity: "error" });
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to download template";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  const handleDownloadTemplate = async (typeId) => {
    // If asking for the generic 'users' template, require selecting a concrete user subtype first
    if (typeId === 'users') {
      setPendingDownload(true);
      setImportTypeSelectorOpen(true);
      return;
    }

    // otherwise, download immediately
    await performDownload(typeId, typeId);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setReportDialogOpen(true);
  };

  const handleDownloadReport = async (report, format = "csv") => {
    try {
      const full = await importService.downloadFullReport(report.id);
      const fullBlob = new Blob([full.data], { type: full.headers["content-type"] || "application/octet-stream" });
      const fullUrl = URL.createObjectURL(fullBlob);
      const fullA = document.createElement("a");
      fullA.href = fullUrl;
      fullA.download = dispositionFilename(full.headers["content-disposition"] || full.headers["Content-Disposition"]) || `import_${report.id}_full_report.csv`;
      fullA.click();
      URL.revokeObjectURL(fullUrl);
      return;
    } catch (err) {}
    try {
      const res = await importService.downloadErrorReport(report.id);
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers["content-disposition"]?.split("filename=")[1] || `import_report_${report.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    } catch (err) {
      // fallback to client-side CSV generation
    }

    const headers = ["row", "value", "reason"];
    const rows = (report.errors || []).map((e) => [
      typeof e.row === 'object' ? JSON.stringify(e.row) : String(e.row),
      e.value || (typeof e.row === 'object' ? JSON.stringify(e.row) : String(e.row)) || "",
      e.reason || e.error || "",
    ]);

    const csvContent = [
      `Import Report: ${report.fileName}`,
      `Type: ${report.entityType || report.type}`,
      `Date: ${report.uploadedAt ? new Date(report.uploadedAt).toLocaleString() : report.uploadedAt}`,
      `Total: ${report.totalRows} | Success: ${report.successCount || report.success || 0} | Failed: ${report.failedCount || report.failed || 0}`,
      "",
      headers.join(","),
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import_report_${report.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleHistoryPageChange = (_e, value) => {
    setHistoryPage(value);
  };

  const handleReportPageChange = (_e, value) => {
    setReportPage(value);
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const historyFiltered = useMemo(() => {
    const list = historyImportData?.imports || [];
    const q = (historySearchQuery || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (item) =>
        (String(item.fileName || "").toLowerCase().includes(q) ||
          String(item.entityType || "").toLowerCase().includes(q))
    );
  }, [historyImportData, historySearchQuery]);

  const reportFiltered = useMemo(() => {
    const list = reportsImportData?.imports || [];
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (item) =>
        (String(item.fileName || "").toLowerCase().includes(q) ||
          String(item.entityType || "").toLowerCase().includes(q))
    );
  }, [reportsImportData, searchQuery]);

  useEffect(() => {
    setHistoryPage(1);
  }, [historySearchQuery, filterType]);

  useEffect(() => {
    if (filterType === "users") {
      setFilterType("");
      setHistoryPage(1);
    }
  }, [filterType]);

  useEffect(() => {
    setReportPage(1);
  }, [searchQuery, reportDatePreset, reportFrom, reportTo]);

  const templateColumns = {
    institutes: [
      "Institute Name*",
      "Code*",
      "Email*",
      "Phone",
      "Address",
      "City",
      "State",
      "Country",
      "Plan",
      "Status",
    ],
    superadmins: [
      "Name*",
      "Email*",
      "Phone",
      "Password*",
      "Institute",
      "Permissions",
    ],
    departments: [
      "Department Name*",
      "Code*",
      "Institute*",
      "HOD Email",
      "Description",
      "Status",
    ],
    users: [
      "Name*",
      "Email*",
      "Phone",
      "Role*",
      "Institute*",
      "Department",
      "Password*",
    ],
    courses: [
      "Name*",
      "Code*",
      "Duration*",
      "Department",
    ],
    subjects: [
      "Subject Name*",
      "Code*",
      "Course*",
      "Semester",
      "Credits",
      "Type",
    ],
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle color="success" fontSize="small" />;
      case "failed":
        return <Error color="error" fontSize="small" />;
      case "processing":
        return <Warning color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Import Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bulk import data for institutes, users, departments, and more
          </Typography>
        </Box>
        <PermissionGate permissions={ALL_IMPORT_PERMISSION_SLUGS}>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => {
              setSelectedImportType("");
              setImportTypeSelectorOpen(true);
            }}
          >
            New Import
          </Button>
        </PermissionGate>
      </Box>

      {/* Import Type Cards */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 2,
          mb: 3,
          "&::-webkit-scrollbar": {
            height: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.05)",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: 4,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        {importTypes.map((type) => (
          <Card
            key={type.id}
            sx={{
              minWidth: 160,
              maxWidth: 160,
              height: 140,
              cursor: "pointer",
              border: 2,
              borderColor:
                selectedImportType === type.id ? `${type.color}.main` : "transparent",
              "&:hover": { borderColor: `${type.color}.light` },
              display: "flex",
              flexDirection: "column",
            }}
            onClick={() => {
              setSelectedImportType(type.id);
              setUploadDialogOpen(true);
            }}
          >
            <CardContent sx={{ textAlign: "center", py: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Avatar
                sx={{ bgcolor: `${type.color}.main`, mx: "auto", mb: 1, width: 48, height: 48 }}
              >
                {type.icon}
              </Avatar>
              <Typography 
                variant="body2" 
                fontWeight={500}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  textAlign: "center",
                  minHeight: 40,
                }}
              >
                {type.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Imports",
            value: statsPagination.total || 0,
            color: "primary.main",
          },
          { 
            label: "Records Imported", 
            value: statsOverallCounts.successCount || 0, 
            color: "success.main" 
          },
          { 
            label: "Success Rate", 
            value: (statsOverallCounts.totalRows && statsOverallCounts.totalRows > 0)
              ? `${Math.round((statsOverallCounts.successCount / statsOverallCounts.totalRows) * 100)}%`
              : "0%",
            color: "info.main" 
          },
          { 
            label: "Failed Records", 
            value: statsOverallCounts.failedCount || 0, 
            color: "error.main" 
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Import History" />
          <Tab label="Import Reports" />
          <Tab label="Templates" />
        </Tabs>
      </Card>

      {/* Import History */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
              <TextField
                placeholder="Search imports..."
                size="small"
                sx={{ width: 300 }}
                value={historySearchQuery}
                onChange={(e) => setHistorySearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setHistoryPage(1);
                  }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {importTypes
                    .filter((t) => t.id !== "users")
                    .map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ position: "relative" }}>
            <Backdrop sx={{ position: "absolute", zIndex: 2, borderRadius: 1, backgroundColor: "rgba(255,255,255,0.65)" }} open={historyFetching && !historyLoading}>
              <CircularProgress color="primary" />
            </Backdrop>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography>Loading import history...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : !historyFetching && historyFiltered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography>No import history found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    historyFiltered.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <Description color="primary" />
                            <Typography variant="body2">
                              {item.fileName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={item.entityType} size="small" />
                        </TableCell>
                        <TableCell>{item.uploadedBy}</TableCell>
                        <TableCell>
                          {new Date(item.uploadedAt).toLocaleDateString()} {new Date(item.uploadedAt).toLocaleTimeString()}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={((item.successCount || 0) / (item.totalRows || 1)) * 100}
                              sx={{ width: 100, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption">
                              {item.successCount || 0}/{item.totalRows || 0}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(item.status)}
                            label={item.status}
                            size="small"
                            color={
                              item.status === "completed" ? "success" : 
                              item.status === "completed_with_errors" ? "warning" : "error"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-start", flexWrap: "nowrap", alignItems: "center", whiteSpace: "nowrap" }}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewReport(item)} color="primary">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(effectiveSuccessCount(item) > 0 || (item.failedCount || 0) > 0 || item.status === "completed" || item.status === "completed_with_errors") && (
                              <Tooltip title="Download Full Report">
                                <IconButton size="small" onClick={() => handleDownloadReport(item)} color="success">
                                  <FileDownload fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap", gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Rows</InputLabel>
                  <Select label="Rows" value={historyLimit} onChange={(e) => { setHistoryLimit(Number(e.target.value)); setHistoryPage(1); }}>
                    {PAGINATION_PAGE_SIZE_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Pagination
                  count={Math.max(1, historyPagination.totalPages || 1)}
                  page={historyPage}
                  onChange={handleHistoryPageChange}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Import Reports */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            {/* Search and Filter */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                placeholder="Search by filename or type..."
                size="small"
                sx={{ width: 300 }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Button startIcon={<Refresh />} variant="outlined" onClick={() => queryClient.invalidateQueries({ queryKey: queryKeys.import.history() })}>
                Refresh
              </Button>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Date range:
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={reportDatePreset}
                onChange={(_e, v) => v != null && setReportDatePreset(v)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {reportDatePreset === "custom" && (
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={reportFrom}
                  onChange={(e) => setReportFrom(e.target.value)}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={reportTo}
                  onChange={(e) => setReportTo(e.target.value)}
                />
              </Box>
            )}

            {/* Import Reports List */}
            <Box sx={{ position: "relative" }}>
            <Backdrop sx={{ position: "absolute", zIndex: 2, borderRadius: 1, backgroundColor: "rgba(255,255,255,0.65)" }} open={reportsFetching && !reportsLoading}>
              <CircularProgress color="primary" />
            </Backdrop>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.100" }}>
                    <TableCell>
                      <strong>File Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Success</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Failed</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Success Rate</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportsLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography>Loading import reports...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                  reportFiltered.map((report) => (
                    <TableRow
                      key={report.id}
                      sx={{ "&:hover": { bgcolor: "action.hover" } }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Description color="primary" fontSize="small" />
                          <Typography variant="body2">
                            {report.fileName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={report.entityType} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {report.uploadedAt ? new Date(report.uploadedAt).toLocaleString() : report.uploadedAt}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={500}>
                          {report.totalRows}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight={500}
                        >
                          {report.successCount || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color={
                            (report.failedCount || 0) > 0 ? "error.main" : "text.secondary"
                          }
                          fontWeight={500}
                        >
                          {report.failedCount || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                            <LinearProgress
                            variant="determinate"
                            value={((report.successCount || 0) / (report.totalRows || 1)) * 100}
                            sx={{
                              width: 60,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: "grey.200",
                              "& .MuiLinearProgress-bar": {
                                bgcolor:
                                  (report.failedCount || 0) > 0
                                    ? "warning.main"
                                    : "success.main",
                              },
                            }}
                          />
                          <Typography variant="caption">
                            {Math.round(
                              ((report.successCount || 0) / (report.totalRows || 1)) * 100
                            )}
                            %
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            report.status === "completed" ? (
                              <CheckCircle fontSize="small" />
                            ) : (
                              <AccessTime fontSize="small" />
                            )
                          }
                          label={report.status}
                          size="small"
                          color={
                            report.status === "completed"
                              ? "success"
                              : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View Report Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewReport(report)}
                              color="primary"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(effectiveSuccessCount(report) > 0 || (report.failedCount || 0) > 0 || report.status === "completed" || report.status === "completed_with_errors") && (
                            <Tooltip title="Download Full Report">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadReport(report)}
                                color="success"
                              >
                                <FileDownload fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap", gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Rows</InputLabel>
                <Select label="Rows" value={reportLimit} onChange={(e) => { setReportLimit(Number(e.target.value)); setReportPage(1); }}>
                  {PAGINATION_PAGE_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Pagination
                count={Math.max(1, reportsPagination.totalPages || 1)}
                page={reportPage}
                onChange={handleReportPageChange}
                color="primary"
                shape="rounded"
              />
            </Box>

            {!reportsFetching && !reportsLoading && reportFiltered.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  No import reports found
                </Typography>
              </Box>
            )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Templates */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          {importTypes.map((type) => (
            <Grid item xs={12} md={6} key={type.id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Avatar sx={{ bgcolor: `${type.color}.main` }}>
                      {type.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {type.label} Template
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {templateColumns[type.id].length} columns
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}
                  >
                    {templateColumns[type.id].map((col, i) => (
                      <Chip
                        key={i}
                        label={col}
                        size="small"
                        variant="outlined"
                        color={col.includes("*") ? "primary" : "default"}
                      />
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    size="small"
                    fullWidth
                  onClick={() => handleDownloadTemplate(type.id)}
                  >
                    Download Template
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Import Wizard */}
      <DataImportWizard
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        entityType={selectedImportType || undefined}
        onImportComplete={() => {
          setUploadDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: queryKeys.import.history() });
        }}
      />

      <ImportTypeSelector
        open={importTypeSelectorOpen}
        forUserSubtypes={pendingDownload}
        onClose={() => {
          setImportTypeSelectorOpen(false);
          setPendingDownload(false);
        }}
        onSelect={(type) => {
          if (pendingDownload) {
            // user chose a concrete subtype to download template for
            setImportTypeSelectorOpen(false);
            setPendingDownload(false);
            // Import wizard should not show "User Type" dropdown for known subtypes.
            // Map role-like subtypes into concrete import entity types.
            // Backend import supports: teachers, students, superadmins, institutes, departments...
            const mappedImportType =
              type.key === "students" ? "students" : type.key === "superadmins" ? "superadmins" : "teachers";

            performDownload(mappedImportType, mappedImportType);
            setSelectedImportType(mappedImportType);
            setUploadDialogOpen(true);
            return;
          }

          // regular flow: open upload wizard for the selected type
          setSelectedImportType(type.key);
          setImportTypeSelectorOpen(false);
          setUploadDialogOpen(true);
        }}
      />

      {/* Report Details Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setReportDialogOpen(false); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6">Import Report Details</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedReport?.fileName}
              </Typography>
            </Box>
            <IconButton onClick={() => setReportDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <>
              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                      <Typography variant="h5" fontWeight={700}>
                        {selectedReport.totalRows}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Rows
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined" sx={{ borderColor: "success.main" }}>
                      <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          color="success.main"
                        >
                          {selectedReport.successCount || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Successful
                        </Typography>
                      </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined" sx={{ borderColor: "error.main" }}>
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="error.main"
                      >
                          {selectedReport.failedCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Failed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined" sx={{ borderColor: "info.main" }}>
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="info.main"
                      >
                        {Math.round(
                          ((selectedReport.successCount || 0) / (selectedReport.totalRows || 1)) *
                            100
                        )}
                        %
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Success Rate
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Error Details */}
              {selectedReport.errors && selectedReport.errors.length > 0 ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Error Details ({selectedReport.errors.length} errors)
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<FileDownload />}
                      variant="outlined"
                      color="error"
                      onClick={() => handleDownloadReport(selectedReport)}
                    >
                      Download Error Report
                    </Button>
                  </Box>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The following records failed during import. You can download
                    the error report and fix the issues before re-importing.
                  </Alert>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    {(() => {
                      const rowObjects = selectedReport.errors.filter(e => e && typeof e.row === 'object');
                      const rowKeys = rowObjects.length > 0 ? Object.keys(rowObjects[0].row) : [];

                      if (rowKeys.length > 0) {
                        return (
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                {rowKeys.map((k) => (
                                  <TableCell key={k} sx={{ fontWeight: 600 }}>{k}</TableCell>
                                ))}
                                <TableCell sx={{ fontWeight: 600 }}>Error Message</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedReport.errors.slice(0, 100).map((error, i) => (
                                <TableRow key={i} sx={{ "&:hover": { bgcolor: "error.lighter" } }}>
                                  <TableCell>{i + 1}</TableCell>
                                  {rowKeys.map((k) => (
                                    <TableCell key={k}>
                                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                        {error.row && typeof error.row === 'object' ? (error.row[k] ?? "") : ""}
                                      </Typography>
                                    </TableCell>
                                  ))}
                                  <TableCell>
                                    <Typography variant="body2" color="error.main">
                                      {error.reason || error.error}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        );
                      }

                      return (
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Row #</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Error Message</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedReport.errors.slice(0, 100).map((error, i) => (
                              <TableRow key={i} sx={{ "&:hover": { bgcolor: "error.lighter" } }}>
                                <TableCell>{i + 1}</TableCell>
                                <TableCell>
                                  <Chip label={error.field || 'row'} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                    {error.value || (typeof error.row === 'string' ? error.row : "(empty)")}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="error.main">{error.reason || error.error}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      );
                    })()}
                  </TableContainer>

                  {selectedReport.errors.length > 100 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Showing first 100 errors. Download the full report to see
                      all {selectedReport.errors.length} errors.
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="success" icon={<CheckCircle />}>
                  All records were imported successfully! No errors found.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
          {selectedReport && (effectiveSuccessCount(selectedReport) > 0 || (selectedReport.failedCount || 0) > 0 || selectedReport.status === "completed" || selectedReport.status === "completed_with_errors") && (
            <Button variant="contained" color="success" startIcon={<FileDownload />} onClick={() => handleDownloadReport(selectedReport)}>
              Download Full Report
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminImport;
